/**
 * TeamSystem Impact - User Management API Client
 * CRUD per gestione utenti admin
 */

import { apiClient } from '../../../api/client';
import type { Utente, Ruolo } from '@teamsystem-impact/shared';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface UserListResponse {
  users: Utente[];
  total: number;
  page: number;
  limit: number;
}

export interface UpdateUserDTO {
  nome?: string;
  cognome?: string;
  lingua?: string;
  stato?: 'attivo' | 'inattivo';
}

export interface UpdateUserRolesDTO {
  ruolo: Ruolo;
  permessi?: string[];
  ambiti?: string[];
}

export interface CreateUserDTO {
  nome: string;
  cognome: string;
  email: string;
  ruolo?: Ruolo;
}

const BASE_PATH = '/users';

export const userApi = {
  create: async (dto: CreateUserDTO): Promise<Utente> => {
    const response = await apiClient.post<ApiResponse<Utente>>(
      BASE_PATH,
      dto
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create user');
    return response.data.data!;
  },

  list: async (filters?: {
    search?: string;
    ruolo?: string;
    stato?: string;
    page?: number;
    limit?: number;
  }): Promise<UserListResponse> => {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.ruolo) params.set('ruolo', filters.ruolo);
    if (filters?.stato) params.set('stato', filters.stato);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));

    const query = params.toString();
    const response = await apiClient.get<ApiResponse<UserListResponse>>(
      `${BASE_PATH}${query ? `?${query}` : ''}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list users');
    return response.data.data!;
  },

  getById: async (id: string): Promise<Utente> => {
    const response = await apiClient.get<ApiResponse<Utente>>(
      `${BASE_PATH}/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get user');
    return response.data.data!;
  },

  update: async (id: string, dto: UpdateUserDTO): Promise<Utente> => {
    const response = await apiClient.put<ApiResponse<Utente>>(
      `${BASE_PATH}/${id}`,
      dto
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update user');
    return response.data.data!;
  },

  updateRoles: async (id: string, dto: UpdateUserRolesDTO): Promise<Utente> => {
    const response = await apiClient.put<ApiResponse<Utente>>(
      `${BASE_PATH}/${id}/roles`,
      dto
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update user roles');
    return response.data.data!;
  },
};
